export const DEFAULT_LOGO_STYLE = {
  enabled: true,
  sizePercent: 18,
  xOffsetPx: 40,
  yOffsetPx: 40,
  shadowAmount: 0,
  iconOpacity: 1,
  background: {
    enabled: false,
    shape: 'rounded-rect',
    color: '#ffffff',
    opacity: 0.92,
    gradientEnabled: false,
    gradientStart: '#ffffff',
    gradientEnd: '#dbeafe',
    gradientAngle: 135,
    paddingPercent: 18,
    borderWidthPercent: 0,
    borderColor: '#ffffff',
  },
};

const VALID_SHAPES = new Set(['rounded-rect', 'square', 'circle', 'pill']);
const LANDSCAPE_RENDER_SIZE = { width: 1264, height: 848 };
const PORTRAIT_RENDER_SIZE = { width: 848, height: 1264 };
export const MAX_POSITION_OFFSET_PX = Math.max(
  LANDSCAPE_RENDER_SIZE.width,
  LANDSCAPE_RENDER_SIZE.height,
  PORTRAIT_RENDER_SIZE.width,
  PORTRAIT_RENDER_SIZE.height
);

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function toNumber(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function sanitizeHexColor(value, fallback) {
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim();
  return /^#([0-9a-fA-F]{6})$/.test(trimmed) ? trimmed : fallback;
}

function getShadowMetrics(shadowAmount, referenceSize) {
  const strength = clamp(shadowAmount, 0, 200) / 200;
  const primaryBlur = Math.max(6, Math.round(referenceSize * (0.1 + (strength * 0.36))));
  const primaryOffsetY = Math.max(2, Math.round(referenceSize * (0.03 + (strength * 0.13))));
  const primaryOpacity = (0.18 + (strength * 0.34)).toFixed(2);
  const secondaryBlur = Math.max(3, Math.round(primaryBlur * 0.48));
  const secondaryOffsetY = Math.max(1, Math.round(primaryOffsetY * 0.45));
  const secondaryOpacity = (0.12 + (strength * 0.22)).toFixed(2);

  return {
    primaryBlur,
    primaryOffsetY,
    primaryOpacity,
    secondaryBlur,
    secondaryOffsetY,
    secondaryOpacity,
  };
}

export function normalizeLogoStyleConfig(input = {}) {
  const backgroundInput = input.background || {};
  const defaults = DEFAULT_LOGO_STYLE.background;
  const shape = VALID_SHAPES.has(backgroundInput.shape) ? backgroundInput.shape : defaults.shape;

  return {
    enabled: input.enabled !== false,
    sizePercent: clamp(toNumber(input.sizePercent, DEFAULT_LOGO_STYLE.sizePercent), 8, 42),
    xOffsetPx: clamp(toNumber(input.xOffsetPx, DEFAULT_LOGO_STYLE.xOffsetPx), 0, MAX_POSITION_OFFSET_PX),
    yOffsetPx: clamp(toNumber(input.yOffsetPx, DEFAULT_LOGO_STYLE.yOffsetPx), 0, MAX_POSITION_OFFSET_PX),
    shadowAmount: clamp(toNumber(input.shadowAmount, DEFAULT_LOGO_STYLE.shadowAmount), 0, 200),
    iconOpacity: DEFAULT_LOGO_STYLE.iconOpacity,
    background: {
      enabled: false,
      shape,
      color: sanitizeHexColor(backgroundInput.color, defaults.color),
      opacity: clamp(toNumber(backgroundInput.opacity, defaults.opacity), 0, 1),
      gradientEnabled: backgroundInput.gradientEnabled === true,
      gradientStart: sanitizeHexColor(backgroundInput.gradientStart, defaults.gradientStart),
      gradientEnd: sanitizeHexColor(backgroundInput.gradientEnd, defaults.gradientEnd),
      gradientAngle: clamp(toNumber(backgroundInput.gradientAngle, defaults.gradientAngle), 0, 360),
      paddingPercent: clamp(toNumber(backgroundInput.paddingPercent, defaults.paddingPercent), 0, 60),
      borderWidthPercent: clamp(toNumber(backgroundInput.borderWidthPercent, defaults.borderWidthPercent), 0, 12),
      borderColor: sanitizeHexColor(backgroundInput.borderColor, defaults.borderColor),
    },
  };
}

function getRenderReferenceSize(frameWidth, frameHeight) {
  return frameWidth >= frameHeight ? LANDSCAPE_RENDER_SIZE : PORTRAIT_RENDER_SIZE;
}

export function getPositionControlValue(configInput, axis) {
  const config = normalizeLogoStyleConfig(configInput);
  return axis === 'x' ? Math.round(config.xOffsetPx) : Math.round(config.yOffsetPx);
}

export function hexToRgbaString(hex, opacity = 1) {
  const safeHex = sanitizeHexColor(hex, '#ffffff');
  const safeOpacity = clamp(opacity, 0, 1);
  const normalized = safeHex.slice(1);
  const red = parseInt(normalized.slice(0, 2), 16);
  const green = parseInt(normalized.slice(2, 4), 16);
  const blue = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${safeOpacity})`;
}

export function getShapeBorderRadius(shape, width, height) {
  switch (shape) {
    case 'circle':
      return '50%';
    case 'pill':
      return `${Math.round(height / 2)}px`;
    case 'square':
      return '0px';
    case 'rounded-rect':
    default:
      return `${Math.round(Math.min(width, height) * 0.22)}px`;
  }
}

export function getLogoPreviewMetrics(configInput, frameWidth, frameHeight, logoAspectRatio = 1) {
  const config = normalizeLogoStyleConfig(configInput);
  const renderReferenceSize = getRenderReferenceSize(frameWidth, frameHeight);
  const shortestSide = Math.min(frameWidth, frameHeight);
  const logoMaxDimension = Math.max(40, Math.round(shortestSide * (config.sizePercent / 100)));
  const safeAspectRatio = logoAspectRatio > 0 ? logoAspectRatio : 1;

  let logoWidth = logoMaxDimension;
  let logoHeight = logoMaxDimension;

  if (safeAspectRatio >= 1) {
    logoWidth = logoMaxDimension;
    logoHeight = Math.max(1, Math.round(logoMaxDimension / safeAspectRatio));
  } else {
    logoHeight = logoMaxDimension;
    logoWidth = Math.max(1, Math.round(logoMaxDimension * safeAspectRatio));
  }

  const padding = config.background.enabled
    ? Math.round(logoMaxDimension * (config.background.paddingPercent / 100))
    : 0;

  let badgeWidth = logoWidth + (padding * 2);
  let badgeHeight = logoHeight + (padding * 2);
  let logoLeft = padding;
  let logoTop = padding;

  if (config.background.enabled && config.background.shape === 'circle') {
    const badgeSize = Math.ceil(Math.sqrt((logoWidth * logoWidth) + (logoHeight * logoHeight)) + (padding * 2));
    badgeWidth = badgeSize;
    badgeHeight = badgeSize;
    logoLeft = Math.round((badgeSize - logoWidth) / 2);
    logoTop = Math.round((badgeSize - logoHeight) / 2);
  } else if (config.background.enabled && config.background.shape === 'square') {
    const badgeSize = Math.max(badgeWidth, badgeHeight);
    badgeWidth = badgeSize;
    badgeHeight = badgeSize;
    logoLeft = Math.round((badgeSize - logoWidth) / 2);
    logoTop = Math.round((badgeSize - logoHeight) / 2);
  }

  const maxLeft = Math.max(0, frameWidth - badgeWidth);
  const maxTop = Math.max(0, frameHeight - badgeHeight);

  return {
    badgeWidth,
    badgeHeight,
    logoWidth,
    logoHeight,
    logoLeft,
    logoTop,
    left: clamp(Math.round((config.xOffsetPx / renderReferenceSize.width) * frameWidth), 0, maxLeft),
    top: clamp(Math.round((config.yOffsetPx / renderReferenceSize.height) * frameHeight), 0, maxTop),
  };
}

export function getDropShadowCss(shadowAmount, referenceSize) {
  if (!shadowAmount) return 'none';
  const shadow = getShadowMetrics(shadowAmount, referenceSize);
  return `drop-shadow(0px ${shadow.primaryOffsetY}px ${shadow.primaryBlur}px rgba(2, 6, 23, ${shadow.primaryOpacity})) drop-shadow(0px ${shadow.secondaryOffsetY}px ${shadow.secondaryBlur}px rgba(2, 6, 23, ${shadow.secondaryOpacity}))`;
}

export function getBadgeShadowCss(shadowAmount, referenceSize) {
  if (!shadowAmount) return 'none';
  const shadow = getShadowMetrics(shadowAmount, referenceSize);
  return `0px ${shadow.primaryOffsetY}px ${shadow.primaryBlur}px rgba(2, 6, 23, ${shadow.primaryOpacity}), 0px ${shadow.secondaryOffsetY}px ${shadow.secondaryBlur}px rgba(2, 6, 23, ${shadow.secondaryOpacity})`;
}
